import { create, tsx } from '@dojo/framework/core/vdom';
import * as css from './Home.m.css';
import { Head } from '../widgets/Head';
import { Section } from '../widgets/Section';
import { ColumnSummary } from '../widgets/ColumnSummary';
import { ConnectStrip } from '../widgets/ConnectStrip';
import { ContentWrapper } from '../widgets/ContentWrapper';
import { Link } from '@dojo/framework/routing/Link';
import { Image } from '../widgets/Image';
import { LinkButton } from '../widgets/LinkButton';
import { Text } from '../widgets/Text';
import Page from '../widgets/Page';
import HomeHeroBackground from '../widgets/HomeHeroBackground';
import ClippedImageSummary from '../widgets/ClippedImageSummary';

const heroImg = require('../assets/images/home/home-main-graphic-with-bg.png');
const heroImgHighRes = require('../assets/images/home/home-main-graphic-with-bg@2x.png');
const logoLayoutImg = require('../assets/images/home/home-logo-layout.svg');
const architectureImg = require('../assets/images/home/home-partner-image-architecture.svg');
const cloudImg = require('../assets/images/home/home-partner-image-cloud.svg');
const fullstackImg = require('../assets/images/home/home-partner-image-full-stack.svg');
const qualityImg = require('../assets/images/home/home-partner-image-quality.svg');
const strategyImg = require('../assets/images/home/home-partner-image-strategy.svg');
const uxImg = require('../assets/images/home/home-partner-image-ui-ux.svg');
const partnerMockupImg = require('../assets/images/home/home-partner-mockup.png');
const partnerMockupImgHighRes = require('../assets/images/home/home-partner-mockup@2x.png');
const stackLogosImg = require('../assets/images/home/home-stack-logos.svg');
const calendarIcon = require('../assets/images/icon-solid/calendar-today-outline.svg');
const flagOutlineIcon = require('../assets/images/icon-solid/flag-outline.svg');
const fullstackOutlineIcon = require('../assets/images/icon-solid/fullstack.svg');
const startCircleIcon = require('../assets/images/icon-solid/star-circle-outline.svg');

const factory = create();

export const Home = factory(function Home() {
	return (
		<Page fade>
			{{
				head: (
					<Head
						title="Enterprise Level Software Development Company | SitePen"
						description="SitePen pioneered Enterprise JavaScript development. We focus on building web applications the right way, the first time. Contact us to learn how we can help your organization."
					/>
				),
				hero: {
					background: <HomeHeroBackground />,
					content: (
						<ClippedImageSummary collapse="hide-medium" contentRatio={0.45}>
							{{
								title: (
									<div classes={[css.heroTitle]}>
										<Text type="hero-alternate" responsive="callout-head">
											We Build Enterprise
											<br />
											Web Apps
										</Text>
									</div>
								),
								blurb: (
									<Text type="large-body" responsive="body">
										You have complex problems that need to be solved the right
										way the first time. Partnering with us will deliver you the
										expertise, leadership, and guidance to connect your software
										strategy to product execution.
									</Text>
								),
								button: (
									<div classes={css.buttonRow}>
										<LinkButton to="contact" rel="noindex">
											Work with us
										</LinkButton>
										<LinkButton to="services" color="on-dark" rel="noindex">
											Explore our services
										</LinkButton>
									</div>
								),
								images: {
									main: (
										<div classes={[css.heroImage]}>
											<Image
												src={heroImg}
												highResSrc={heroImgHighRes}
												width={650}
												height={696}
											/>
										</div>
									)
								}
							}}
						</ClippedImageSummary>
					)
				},
				page: (
					<virtual>
						<Section type="leading">
							<div classes={[css.provenExcellence]}>
								<ClippedImageSummary
									reverse
									collapse="stack-medium"
									contentRatio={0.6}
								>
									{{
										title: <Text type="section">Proven Excellence</Text>,
										blurb:
											'From proofs-of-concept to fullstack feature development to legacy re-engineering, companies rely on our agility and expertise in all areas of the software development lifecycle. Learn how we help our customers meet their development objectives successfully and with confidence.',
										button: (
											<LinkButton to="work" rel="noindex">
												View our work
											</LinkButton>
										),
										images: {
											main: (
												<Image
													src={logoLayoutImg}
													width={894}
													height={222}
												/>
											)
										}
									}}
								</ClippedImageSummary>
							</div>
						</Section>
						<Section>
							<ContentWrapper>
								<ColumnSummary animate>
									{{
										title: 'Your Partner in Anything',
										blurb:
											'As leaders in enterprise application development and with our position at the forefront of the modern web, we offer a partnership that will optimize your approach in all areas of development and instill confidence in meeting your company’s objectives.',
										items: [
											{
												title: 'Full Stack JS Development',
												blurb: (
													<virtual>
														JavaScript is no longer confined to web
														browsers and neither are we. Our deep and
														extensive expertise allows us to produce
														exceptional results wherever JavaScript
														runs.{' '}
														<Text bold>
															<Link to="expertiseFullStack">
																Learn more about Full Stack JS
																Development
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={fullstackImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											},
											{
												title: 'Enterprise UI/UX Design',
												blurb: (
													<virtual>
														We design user experiences that consider
														visual design, interaction design,
														information architecture, usability, and
														accessibility to provide software that is
														frictionless to use.{' '}
														<Text bold>
															<Link to="servicesEnterpriseUX">
																Learn more about Enterprise UI/UX
																Design
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={uxImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											},
											{
												title: 'Enterprise Architecture',
												blurb: (
													<virtual>
														SitePen’s expertise in end-to-end solution
														delivery provides strong pillars in
														enterprise architecture design,
														implementation, and maintenance.{' '}
														<Text bold>
															<Link to="servicesEnterpriseArchitecture">
																Learn more about Enterprise
																Architecture
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={architectureImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											},
											{
												title: 'Software Quality Assurance',
												blurb: (
													<virtual>
														Our focus is on quality outcomes. Our
														commitment to delivering usable,
														high-performance solutions to delight our
														customer’s customers is at the core of all
														we do.{' '}
														<Text bold>
															<Link to="servicesQualityAssurance">
																Learn more about Software Quality
																Assurance
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={qualityImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											},
											{
												title: 'Process & Technical Strategy',
												blurb: (
													<virtual>
														SitePen champions agile engineering
														practices for robust ongoing delivery that
														incorporates the wider technology and
														business strategies of your organization to
														get the best outcomes for all stakeholders.{' '}
														<Text bold>
															<Link to="servicesEnterpriseTechnologyStrategy">
																Learn more about Process & Technical
																Strategy
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={strategyImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											},
											{
												title: 'Enterprise Cloud Services',
												blurb: (
													<virtual>
														We work with business, technology, and
														operations stakeholders in your organization
														to help design and implement cloud-first
														strategies and solutions.{' '}
														<Text bold>
															<Link to="servicesEnterpriseCloudServices">
																Learn more about Enterprise Cloud
																Services
															</Link>
														</Text>
													</virtual>
												),
												image: (
													<Image
														src={cloudImg}
														size="sm"
														lazy
														height={200}
														width={290}
													/>
												)
											}
										]
									}}
								</ColumnSummary>
							</ContentWrapper>
						</Section>

						<Section type="leading">
							<div classes={css.greenBackground}>
								<div classes={[css.anyStackContainer]}>
									<ClippedImageSummary
										collapse="stack-reverse-medium"
										contentRatio={0.6}
									>
										{{
											title: 'Experience with Any Stack',
											blurb:
												'We’re no strangers to JavaScript. SitePen has been building with JS for over 15 years and we are experts in the modern landscape, legacy frameworks and digital transformation. Whether you’re building something new or migrating to a new platform, we have the experience to help.',
											button: (
												<virtual>
													<div classes={css.buttonRow}>
														<LinkButton to="expertise">
															Our expertise
														</LinkButton>
														<LinkButton
															to="expertiseJavascript"
															color="on-dark"
														>
															JavaScript services
														</LinkButton>
													</div>
												</virtual>
											),
											images: {
												main: (
													<div classes={[css.anyStackImage]}>
														<Image
															src={stackLogosImg}
															lazy
															responsive={{
																medium: {
																	size: 'lg',
																	align: 'center'
																}
															}}
															size="xl"
															width={325}
															height={270}
														/>
													</div>
												)
											}
										}}
									</ClippedImageSummary>
								</div>
							</div>
						</Section>

						<Section type="leading">
							<ClippedImageSummary reverse collapse="stack-large">
								{{
									title: 'A Partner You Can Rely On',
									blurb: (
										<virtual>
											<Text type="body">
												Benefit from clarity of communication and a
												partnership based on shared goals, knowledge
												transfer, and trust.
											</Text>
											<ul classes={['fade-section']}>
												<li classes={[css.partnerItem, 'fade']}>
													<img
														src={fullstackOutlineIcon}
														classes={css.partnerIcon}
														loading="lazy"
														alt=""
													/>
													<Text type="subhead">Enterprise Expertise</Text>
													<Text type="body">
														Align with people who provide a rare
														combination of enterprise experience,{' '}
														<Text bold>
															<Link to="expertise">
																software development expertise
															</Link>
														</Text>
														, and the skills to get it done.
													</Text>
												</li>
												<li classes={[css.partnerItem, 'fade']}>
													<img
														src={flagOutlineIcon}
														classes={css.partnerIcon}
														loading="lazy"
														alt=""
													/>
													<Text type="subhead">Industry Leadership</Text>
													<Text type="body">
														Be in sync with leaders in Enterprise
														JavaScript and{' '}
														<Text bold>
															<Link to="about">work with a team</Link>
														</Text>{' '}
														that understands and commands the
														complexities of today’s technical landscape.
													</Text>
												</li>
												<li classes={[css.partnerItem, 'fade']}>
													<img
														src={calendarIcon}
														classes={css.partnerIcon}
														loading="lazy"
														alt=""
													/>
													<Text type="subhead">Predictability</Text>
													<Text type="body">
														Rely on timely delivery with predictably
														dependable results from a complete team that
														can do it all, end-to-end.
													</Text>
												</li>
												<li classes={[css.partnerItem, 'fade']}>
													<img
														src={startCircleIcon}
														classes={css.partnerIcon}
														loading="lazy"
														alt=""
													/>
													<Text type="subhead">
														Great Customer Experience
													</Text>
													<Text type="body">
														Reach your goals with a company that
														develops both applications and partner
														relationships with the user’s experience in
														mind.
													</Text>
												</li>
											</ul>
										</virtual>
									),
									images: {
										main: (
											<div classes={[css.partnerImage]}>
												<Image
													src={partnerMockupImg}
													highResSrc={partnerMockupImgHighRes}
													width={894}
													height={751}
													lazy
													shadow="light"
													align="center"
												/>
											</div>
										)
									}
								}}
							</ClippedImageSummary>
						</Section>

						<Section type="trailing">
							<div classes={[css.homeConnect]}>
								<ConnectStrip trailing />
							</div>
						</Section>
					</virtual>
				)
			}}
		</Page>
	);
});

export default Home;
